import { useTranslation } from '@jq4race/i18n';
import {
  type BulkAddEventGuestsRequest,
  BulkAddEventGuestsRequestSchema,
  type CreateEventGuestRequest,
} from '@jq4race/shared';
import { Button, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface BulkImportGuestsFormProps {
  onSubmit: (input: BulkAddEventGuestsRequest) => Promise<void>;
  disabled?: boolean;
}

interface ParsedGuestLines {
  guests: CreateEventGuestRequest[];
  errors: string[];
}

/**
 * Parses pasted guest lines into guest records (R2.1 bulk pre-load).
 * Accepts one guest per line, `name, plate` (comma OR tab separated) or a
 * bare name with no plate. Blank lines are skipped. A line with no name
 * (e.g. `, ABC-123`) is reported as an error and excluded from the batch.
 *
 * NOTE: error messages are returned in English regardless of UI locale —
 * this is a pure parsing helper with no access to the i18n context. The
 * component below re-derives the same "missing a name" copy via t() for
 * display; this raw list is only used for the `errors.length` gate.
 */
export function parseGuestLines(text: string): ParsedGuestLines {
  const guests: CreateEventGuestRequest[] = [];
  const errors: string[] = [];

  const lines = text.split(/\r?\n/);
  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) return;

    const [namePart, platePart] = line.split(/[,\t]/, 2);
    const name = namePart?.trim() ?? '';
    const plate = platePart?.trim();

    if (!name) {
      errors.push(`Line ${index + 1}: missing a name`);
      return;
    }

    guests.push({ name, plate: plate ? plate : null });
  });

  return { guests, errors };
}

export function BulkImportGuestsForm({ onSubmit, disabled }: BulkImportGuestsFormProps) {
  const { t } = useTranslation('events');
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { guests, errors } = useMemo(() => parseGuestLines(text), [text]);
  const isDisabled = disabled || isSubmitting;
  const canSubmit = !isDisabled && guests.length > 0 && errors.length === 0;

  async function handleImport() {
    setServerError(null);
    const parsed = BulkAddEventGuestsRequestSchema.safeParse({ guests });
    if (!parsed.success) {
      setServerError(parsed.error.issues[0]?.message ?? t('bulkImportGuestsForm.invalidGuestList'));
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(parsed.data);
      setText('');
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('bulkImportGuestsForm.importError') }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      {serverError && <FormBanner message={serverError} />}

      <div>
        <Label
          htmlFor="guest-bulk-import"
          className="mb-1.5 block text-[13px] font-medium text-jq-charcoal"
        >
          {t('bulkImportGuestsForm.label')}
        </Label>
        <TextArea
          id="guest-bulk-import"
          rows={5}
          placeholder={t('bulkImportGuestsForm.placeholder')}
          value={text}
          disabled={isDisabled}
          onChange={(e) => setText(e.target.value)}
        />
        {errors.length > 0 ? (
          <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
            {errors.join(' · ')}
          </p>
        ) : (
          <p className="mt-1.5 text-[12px] text-jq-graphite">
            {guests.length === 0
              ? t('bulkImportGuestsForm.pasteHint')
              : t('bulkImportGuestsForm.readyToImport', { count: guests.length })}
          </p>
        )}
      </div>

      <Button type="button" size="sm" disabled={!canSubmit} onClick={() => void handleImport()}>
        {isSubmitting
          ? t('bulkImportGuestsForm.importing')
          : guests.length > 0
            ? t('bulkImportGuestsForm.importButton', { count: guests.length })
            : t('bulkImportGuestsForm.importButtonEmpty')}
      </Button>
    </div>
  );
}

const TextArea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextArea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        {...rest}
        className={`flex w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 py-2 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
